import React, { useState, createElement } from 'react';
import { Share2Icon, CopyIcon, GiftIcon, CheckIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/Button';
import { useBookingCart } from '../contexts/BookingCartContext';
export function PromotionScreen() {
  const navigate = useNavigate();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const {
    cartItems
  } = useBookingCart();
  const copyPromoCode = async (code: string) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          setCopiedCode(code);
          setTimeout(() => setCopiedCode(null), 2000);
        } catch (err) {
          console.error('Fallback copy failed:', err);
          alert(`Copy this code: ${code}`);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error('Copy failed:', err);
      // Final fallback - show alert with code
      alert(`Copy this code: ${code}`);
    }
  };
  return <div className="h-screen w-full flex flex-col bg-[#f8f9fa]" data-id="element-762">
      <Header title="Special Offers" showBack={true} onBack={() => navigate(-1)} showCart={cartItems.length > 0} cartItemCount={cartItems.length} onCartClick={() => navigate('/booking/cart')} actionButton={<button className="p-2 hover:bg-gray-100 rounded-full" data-id="element-764">
            <Share2Icon className="w-5 h-5 text-[#1f5070]" data-id="element-765" />
          </button>} data-id="element-763" />

      <div className="flex-1 overflow-auto" data-id="element-766">
        {/* Hero banner */}
        <div className="relative h-48 bg-gradient-to-br from-[#3dc7e3] to-[#c7f61e] overflow-hidden" data-id="element-767">
          <img src="https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=800&h=400&fit=crop" alt="Promotion" className="w-full h-full object-cover opacity-40" data-id="element-768" />
          <div className="absolute top-4 right-4" data-id="element-769">
            <span className="bg-[#E53935] text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-770">
              LIMITED TIME
            </span>
          </div>
        </div>

        <div className="px-4 py-6 space-y-4" data-id="element-771">
          {/* Main promotion card */}
          <div className="bg-white rounded-2xl shadow-lg p-6" data-id="element-772">
            <h1 className="text-2xl font-bold text-[#1f5070] mb-2" data-id="element-773">
              Weekend Special - 20% Off
            </h1>
            <p className="text-lg text-[#3dc7e3] font-bold mb-4" data-id="element-774">
              Valid: Nov 23-24, 2024
            </p>

            <div className="space-y-2 mb-6 text-[#666666]" data-id="element-775">
              <p data-id="element-776">• Book any court this weekend and save 20%</p>
              <p data-id="element-777">• Valid for all time slots</p>
              <p data-id="element-778">• Use code at checkout to apply discount</p>
            </div>

            {/* Promo code box */}
            <div className="border-2 border-dashed border-[#c7f61e] rounded-lg p-4 bg-[#c7f61e]/5 mb-4" data-id="element-779">
              <div className="flex items-center justify-between" data-id="element-780">
                <span className="text-2xl font-bold text-[#1f5070] font-mono" data-id="element-781">
                  WEEKEND20
                </span>
                <button onClick={() => copyPromoCode('WEEKEND20')} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm active:scale-95 transition-all ${copiedCode === 'WEEKEND20' ? 'bg-green-500 text-white' : 'bg-[#3dc7e3] text-white'}`} data-id="element-782">
                  {copiedCode === 'WEEKEND20' ? <>
                      <CheckIcon className="w-4 h-4" data-id="element-783" />
                      Copied!
                    </> : <>
                      <CopyIcon className="w-4 h-4" data-id="element-784" />
                      Copy
                    </>}
                </button>
              </div>
            </div>

            <Button variant="gradient" fullWidth onClick={() => navigate('/home')} data-id="element-785">
              Book Now
            </Button>

            {/* Terms */}
            <details className="mt-4" data-id="element-786">
              <summary className="text-sm text-[#3dc7e3] cursor-pointer font-bold" data-id="element-787">
                Terms & Conditions
              </summary>
              <p className="text-xs text-[#666666] mt-2" data-id="element-788">
                Offer valid for bookings made between Nov 23-24, 2024. Cannot be
                combined with other offers. Subject to availability.
              </p>
            </details>
          </div>

          {/* Other promotions */}
          <div className="space-y-3" data-id="element-789">
            <h2 className="text-lg font-bold text-[#1f5070] px-2" data-id="element-790">
              Other Active Promotions
            </h2>

            <div className="bg-white rounded-xl shadow-md p-4" data-id="element-791">
              <div className="flex gap-4 mb-3" data-id="element-792">
                <div className="w-12 h-12 bg-[#c7f61e]/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-793">
                  <GiftIcon className="w-6 h-6 text-[#c7f61e]" data-id="element-794" />
                </div>
                <div className="flex-1" data-id="element-795">
                  <h3 className="text-base font-bold text-[#1f5070] mb-1" data-id="element-796">
                    Refer a Friend - 100 UC Off
                  </h3>
                  <p className="text-sm text-[#666666] mb-2" data-id="element-797">
                    Get 100 UC discount on your next booking
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-3 flex items-center justify-between" data-id="element-798">
                <span className="text-lg font-bold text-[#1f5070] font-mono" data-id="element-799">
                  REFER100
                </span>
                <button onClick={() => copyPromoCode('REFER100')} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-bold text-sm active:scale-95 transition-all ${copiedCode === 'REFER100' ? 'bg-green-500 text-white' : 'bg-[#3dc7e3] text-white'}`} data-id="element-800">
                  {copiedCode === 'REFER100' ? <>
                      <CheckIcon className="w-4 h-4" data-id="element-801" />
                      Copied
                    </> : <>
                      <CopyIcon className="w-4 h-4" data-id="element-802" />
                      Copy
                    </>}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-4" data-id="element-803">
              <div className="flex gap-4 mb-3" data-id="element-804">
                <div className="w-12 h-12 bg-[#3dc7e3]/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-805">
                  <GiftIcon className="w-6 h-6 text-[#3dc7e3]" data-id="element-806" />
                </div>
                <div className="flex-1" data-id="element-807">
                  <h3 className="text-base font-bold text-[#1f5070] mb-1" data-id="element-808">
                    Happy Hour - 15% Off
                  </h3>
                  <p className="text-sm text-[#666666] mb-2" data-id="element-809">
                    Save on weekday morning bookings
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-3 flex items-center justify-between" data-id="element-810">
                <span className="text-lg font-bold text-[#1f5070] font-mono" data-id="element-811">
                  HAPPYHOUR
                </span>
                <button onClick={() => copyPromoCode('HAPPYHOUR')} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-bold text-sm active:scale-95 transition-all ${copiedCode === 'HAPPYHOUR' ? 'bg-green-500 text-white' : 'bg-[#3dc7e3] text-white'}`} data-id="element-812">
                  {copiedCode === 'HAPPYHOUR' ? <>
                      <CheckIcon className="w-4 h-4" data-id="element-813" />
                      Copied
                    </> : <>
                      <CopyIcon className="w-4 h-4" data-id="element-814" />
                      Copy
                    </>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BottomNav data-id="element-815" />
    </div>;
}